import React, { useState } from 'react';

import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { uiActions } from '../../store/ui-slice';
import { form2Transaction } from '../../store/forms/form2-actions';
import { catalogsActions } from '../../store/catalogs-slice';
import {
  form2BrandsCatalog,
  form2SubbrandsCatalog,
  form2ModelsCatalog,
  form2VersionsCatalog,
} from '../../store/catalogs-actions';

import Button from '../commons/Button';
import FieldSelect from '../commons/FieldSelect';

import { FORM2_VALIDATION_SCHEMA } from '../../constants/validation-schemas';
import { doNavigation } from '../../utils/helper-functions';

const Form2 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.appConfig.appToken);
  const currentStep = useSelector((state) => state.appConfig.quoterCurrentStep);
  const postalCode = useSelector((state) => state.landingPage.postalCode);
  const form2State = useSelector((state) => state.form2);
  const typeVehiclesCatalog = [
    {
      value: 'auto',
      name: 'Autos',
    },
    {
      value: 'pick up',
      name: 'Pick Up',
    },
  ];
  const typeVehicle = useSelector((state) => state.form2.typeVehicle);
  const [selectedTypeVehicle, setSelectedTypeVehicle] = useState(typeVehicle);

  const brand = useSelector((state) => state.form2.brand);
  const brandCatalog = useSelector((state) => state.catalogs.brands);
  const [selectedBrand, setSelectedBrand] = useState(brand);

  const subbrand = useSelector((state) => state.form2.subbrand);
  const subbrandCatalog = useSelector((state) => state.catalogs.subbrands);
  const [selectedSubbrand, setSelectedSubbrand] = useState(subbrand);

  const model = useSelector((state) => state.form2.model);
  const modelCatalog = useSelector((state) => state.catalogs.models);
  const [selectedModel, setSelectedModel] = useState(model);

  const versionCatalog = useSelector((state) => state.catalogs.versions);

  const navigationHandler = (direction) => {
    doNavigation(currentStep, direction, navigate, dispatch, uiActions, true);
  };

  const submitHandler = (values) => {
    const selectedVersion = versionCatalog.find((v) => v.description === values.version);
    dispatch(form2Transaction(
      token,
      selectedVersion.cvegs,
      postalCode,
      selectedTypeVehicle,
      selectedBrand,
      selectedSubbrand,
      selectedModel,
      selectedVersion.description,
      currentStep,
      navigate,
    ));
  };

  const changeVTHandler = (typeVehicleValue, setFieldValue) => {
    setSelectedTypeVehicle(typeVehicleValue);
    if (typeVehicleValue !== '') {
      dispatch(form2BrandsCatalog(token, typeVehicleValue));
    }
    dispatch(catalogsActions.clearForTypeSelection());
    setFieldValue('brand', '');
    setFieldValue('subbrand', '');
    setFieldValue('model', '');
    setFieldValue('version', '');
    setSelectedBrand('');
    setSelectedSubbrand('');
    setSelectedModel('');
  };

  const changeBrandHandler = (brandValue, setFieldValue) => {
    setSelectedBrand(brandValue);
    if (brandValue !== '') {
      dispatch(form2SubbrandsCatalog(token, selectedTypeVehicle, brandValue));
    }
    dispatch(catalogsActions.clearForBrandSelection());
    setFieldValue('subbrand', '');
    setFieldValue('model', '');
    setFieldValue('version', '');
    setSelectedSubbrand('');
    setSelectedModel('');
  };

  const changeSubbrandHandler = (subbrandValue, setFieldValue) => {
    setSelectedSubbrand(subbrandValue);
    if (subbrandValue !== '') {
      dispatch(form2ModelsCatalog(token, selectedTypeVehicle, selectedBrand, subbrandValue));
    }
    dispatch(catalogsActions.clearForSubbrandSelection());
    setFieldValue('model', '');
    setFieldValue('version', '');
    setSelectedModel('');
  };

  const changeModelHandler = (modelValue, setFieldValue) => {
    setSelectedModel(modelValue);
    if (modelValue !== '') {
      dispatch(form2VersionsCatalog(
        token,
        selectedTypeVehicle,
        selectedBrand,
        selectedSubbrand,
        modelValue,
      ));
    }
    dispatch(catalogsActions.clearForModelSelection());
    setFieldValue('version', '');
  };

  return (
    <Formik
      onSubmit={submitHandler}
      enableReinitialize
      initialValues={form2State}
      validateOnChange
      validateOnMount={!form2State.wasValidatedBefore}
      validationSchema={Yup.object().shape(FORM2_VALIDATION_SCHEMA)}>
      {({ isValid }) => {
        return (
          <Form className="quoter-form">
            <h2 className="form-title text-center">
              Datos del vehículo
            </h2>

            <p className="form-subtitle text-center">
              <span>Ingresa los datos inciales del vehículo para calcular el costo del seguro</span>
              <br />
              <span className="color-red">*&nbsp;</span>
              Campos obligatorios
            </p>

            <div className="form-fields">
              <div className="field-row">
                <FieldSelect
                  onChangeCallback={changeVTHandler}
                  label="Tipo de vehículo"
                  name="typeVehicle"
                  required
                  list={typeVehiclesCatalog}
                  keyName="value"
                  keyLabel="name" />
                <FieldSelect
                  onChangeCallback={changeBrandHandler}
                  label="Marca"
                  name="brand"
                  required
                  placeholder={brandCatalog.length ? 'Selecciona marca' : 'Selecciona el campo anterior'}
                  isDisabled={brandCatalog.length === 0}
                  list={brandCatalog}
                  keyName="name" />
              </div>
              <div className="field-row">
                <FieldSelect
                  onChangeCallback={changeSubbrandHandler}
                  label="Submarca"
                  name="subbrand"
                  required
                  isDisabled={subbrandCatalog.length === 0}
                  placeholder={subbrandCatalog.length ? 'Selecciona submarca' : 'Selecciona el campo anterior'}
                  list={subbrandCatalog}
                  keyName="name" />
                <FieldSelect
                  onChangeCallback={changeModelHandler}
                  label="Modelo"
                  name="model"
                  required
                  list={modelCatalog}
                  placeholder={modelCatalog.length ? 'Selecciona modelo' : 'Selecciona el campo anterior'}
                  isDisabled={modelCatalog.length === 0}
                  keyName="name" />
              </div>
              <div className="field-row">
                <FieldSelect
                  label="Versión"
                  name="version"
                  required
                  list={versionCatalog}
                  placeholder={versionCatalog.length ? 'Selecciona versión' : 'Selecciona el campo anterior'}
                  isDisabled={versionCatalog.length === 0}
                  keyName="description" />
              </div>
            </div>
            { /** /.form-rows */}
            <div className="form-buttons">
              <Button
                onClick={() => navigationHandler('backward')}
                type="button"
                label="Regresar"
                buttonClasses="-secondary"
                iconPosition="before"
                iconClasses="arrow-left" />
              <Button
                type="submit"
                disabled={!isValid}
                label="Continuar"
                iconClasses="arrow-right" />
            </div>
            <p className="mt-50">
              En caso de no encontrar la versión de tu vehículo, podemos asistirte en línea, llama al&nbsp;
              <a href="tel:5571003159"><strong>55 7100 3159</strong></a>
              &nbsp;para finalizar tu solicitud.&nbsp;
              <strong>Horarios de atención: </strong>
              Lunes a Viernes de 8 a 21 horas y sábados de 9 a 17 hrs
            </p>
          </Form>
        );
      }}
    </Formik>
  );
};

export default Form2;
